import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col, Card, Table, Form, Button, Spinner, Alert } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './SalaryManager.css';

const SalaryManager = () => {
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorised: token } };

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLocations();
    fetchEmployees();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get('/api/hr/locations', headers);
      setLocations(res.data || []);
    } catch (err) {
      console.error("Error fetching locations", err);
    }
  };

  const fetchEmployees = async (locId = '') => {
    try {
      const res = await axios.get(`/api/hr/employees${locId ? `?locationId=${locId}` : ''}`, headers);
      // Correction: le backend renvoie directement un tableau d'employés
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching employees", err);
      setError("Impossible de charger la liste des employés");
    }
  };

  const handleLocationChange = (locId) => {
    setSelectedLocation(locId);
    setSelectedEmployee(''); // Reset employee selection when location changes
    fetchEmployees(locId);
  };

  const calculateSalary = async () => {
    if (!selectedEmployee || !selectedMonth) return;
    
    setLoading(true);
    setError(null);
    setSalaryData(null);

    try {
      const [year, month] = selectedMonth.split('-');
      const res = await axios.get(`/api/hr/salary/calculate?matricule=${selectedEmployee}&month=${month}&year=${year}`, headers);
      setSalaryData(res.data);
    } catch (err) {
      console.error("Error calculating salary", err);
      setError(err.response?.data?.message || "Erreur lors du calcul du salaire");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(val);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const generateBulkPDF = async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const res = await axios.get(`/api/hr/salary/calculate-all?month=${month}&year=${year}${selectedLocation ? `&locationId=${selectedLocation}` : ''}`, headers);
      
      const { results, period } = res.data;
      const doc = new jsPDF();
      
      results.forEach((data, index) => {
        if (index > 0) doc.addPage();
        
        // Header
        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text("FICHE DE PAIE", 105, 20, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text(`Période : ${formatDate(period.start)} au ${formatDate(period.end)}`, 105, 28, { align: 'center' });
        
        // Employee Info
        doc.setDrawColor(200);
        doc.line(14, 35, 196, 35);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(`Employé : ${data.employee.name}`, 14, 45);
        doc.setFont("helvetica", "normal");
        doc.text(`Matricule : ${data.employee.matricule}`, 14, 52);
        doc.text(`Poste : ${data.employee.position || '—'}`, 14, 59);
        
        doc.text(`Taux Horaire : ${formatCurrency(data.employee.hourlyRate)}/h`, 140, 45);
        doc.text(`Heures Base : 208h`, 140, 52);
        
        // Table
        const tableBody = [
          ["Heures de base (réelles)", `${data.stats.regularHours}h`, formatCurrency(data.employee.hourlyRate), formatCurrency(data.calculation.regularPay), ""],
        ];
        
        if (data.stats.overtimeHours > 0) {
          tableBody.push(["Heures Supplémentaires (175%)", `${data.stats.overtimeHours}h`, "x1.75", formatCurrency(data.calculation.overtimePay), ""]);
        }
        
        if (data.stats.totalPenaltyHours > 0) {
          tableBody.push(["Retards / Absences / Pauses", `${data.stats.totalPenaltyHours}h`, formatCurrency(data.employee.hourlyRate), "", formatCurrency(data.calculation.penaltyDeduction)]);
        }
        
        doc.autoTable({
          startY: 70,
          head: [['Désignation', 'Nombre', 'Taux', 'Gain', 'Retenue']],
          body: tableBody,
          theme: 'striped',
          headStyles: { fillStyle: 'F', fillColor: [67, 24, 255] },
          foot: [['', '', 'TOTAL NET À PAYER', '', formatCurrency(data.calculation.netSalary)]],
          footStyles: { fillColor: [241, 245, 249], textColor: [0, 0, 0], fontStyle: 'bold' }
        });
        
        // Signatures
        const finalY = doc.lastAutoTable.finalY + 30;
        doc.text("Signature de l'employé", 14, finalY);
        doc.text("Cachet et Signature Direction", 140, finalY);
        doc.line(14, finalY + 15, 60, finalY + 15);
        doc.line(140, finalY + 15, 190, finalY + 15);
      });
      
      doc.save(`Fiches_Paie_${selectedMonth}.pdf`);
    } catch (err) {
      console.error("Error generating bulk PDF", err);
      setError("Erreur lors de la génération du PDF groupé");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="salary-manager">
      <div className="salary-header no-print">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>💰 Gestion des Salaires</h1>
            <p>Calcul automatique basé sur le pointage (Période : 27 au 26)</p>
          </div>
          <div>
            <Button variant="success" className="btn-bulk-pdf" onClick={generateBulkPDF} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : '📄 Générer PDF Groupé (Tous)'}
            </Button>
          </div>
        </div>
      </div>

      <Card className="salary-card mb-4 no-print">
        <Card.Body>
          <Form>
            <Row className="align-items-end g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>📍 Localisation / Site</Form.Label>
                  <Form.Control 
                    as="select" 
                    value={selectedLocation} 
                    onChange={(e) => handleLocationChange(e.target.value)}
                    className="custom-select"
                  >
                    <option value="">Tous les sites</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc._id}>{loc.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>👤 Employé</Form.Label>
                  <Form.Control 
                    as="select" 
                    value={selectedEmployee} 
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="custom-select"
                  >
                    <option value="">-- Choisir un employé --</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp.matricule}>
                        {emp.name} ({emp.matricule})
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>📅 Mois de Paie</Form.Label>
                  <Form.Control 
                    type="month" 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button 
                  className="btn-calculate" 
                  onClick={calculateSalary} 
                  disabled={loading || !selectedEmployee}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : '🚀 Calculer le Salaire'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="no-print">{error}</Alert>}

      {salaryData && (
        <div className="salary-results fade-in">
          {/* Fiche de Paie Layout (Visible on Screen and optimized for Print) */}
          <div className="fiche-paie-container">
            <Card className="salary-card fiche-paie-card">
              <Card.Header className="fiche-header d-flex justify-content-between align-items-center">
                <div className="company-info">
                  <h3 className="m-0">FICHE DE PAIE</h3>
                  <p className="period-label">Période : {formatDate(salaryData.period.start)} au {formatDate(salaryData.period.end)}</p>
                </div>
                <div className="employee-info-header text-right">
                  <h4>{salaryData.employee.name}</h4>
                  <p>Matricule : {salaryData.employee.matricule}</p>
                </div>
              </Card.Header>
              <Card.Body>
                <Row className="mb-4 details-row">
                  <Col sm={6}>
                    <p><strong>Poste :</strong> {salaryData.employee.position || '—'}</p>
                    <p><strong>Département :</strong> {salaryData.employee.department || '—'}</p>
                  </Col>
                  <Col sm={6} className="text-right">
                    <p><strong>Taux Horaire :</strong> {formatCurrency(salaryData.employee.hourlyRate)}/h</p>
                    <p><strong>Heures Base :</strong> {salaryData.stats.baseHoursLimit}h</p>
                  </Col>
                </Row>

                <Table bordered hover className="fiche-table">
                  <thead>
                    <tr className="table-secondary">
                      <th>Désignation</th>
                      <th className="text-center">Nombre / Heures</th>
                      <th className="text-center">Base / Taux</th>
                      <th className="text-right">A Gain</th>
                      <th className="text-right">A Retenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Heures de base (réelles)</td>
                      <td className="text-center">{salaryData.stats.regularHours}h</td>
                      <td className="text-center">{formatCurrency(salaryData.employee.hourlyRate)}/h</td>
                      <td className="text-right">{formatCurrency(salaryData.calculation.regularPay)}</td>
                      <td></td>
                    </tr>
                    {salaryData.stats.overtimeHours > 0 && (
                      <tr>
                        <td>Heures Supplémentaires (175%)</td>
                        <td className="text-center">{salaryData.stats.overtimeHours}h</td>
                        <td className="text-center">x1.75</td>
                        <td className="text-right">{formatCurrency(salaryData.calculation.overtimePay)}</td>
                        <td></td>
                      </tr>
                    )}
                    {salaryData.stats.totalPenaltyHours > 0 && (
                      <tr>
                        <td>Retards / Absences / Pauses</td>
                        <td className="text-center">{salaryData.stats.totalPenaltyHours}h</td>
                        <td className="text-center">{formatCurrency(salaryData.employee.hourlyRate)}</td>
                        <td></td>
                        <td className="text-right">{formatCurrency(salaryData.calculation.penaltyDeduction)}</td>
                      </tr>
                    )}
                    <tr className="empty-row"><td colSpan="5"></td></tr>
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="3" className="text-right"><strong>SALAIRE NET À PAYER</strong></td>
                      <td colSpan="2" className="text-right"><strong>{formatCurrency(salaryData.calculation.netSalary)}</strong></td>
                    </tr>
                  </tfoot>
                </Table>

                <div className="fiche-footer mt-5 d-flex justify-content-between">
                  <div className="signature-box">
                    <p>Signature de l'employé</p>
                    <div className="sign-line"></div>
                  </div>
                  <div className="signature-box">
                    <p>Cachet et Signature Direction</p>
                    <div className="sign-line"></div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <Row className="mt-4 no-print">
            <Col md={12} className="text-center">
              <Button variant="outline-primary" size="lg" onClick={() => window.print()}>
                🖨 Imprimer la Fiche de Paie Détaillée
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default SalaryManager;
